import { memo, useCallback, useState } from "react";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { useUser } from "@/entities/Auth";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { Button } from "@/shared/ui/Button/Button";
import { Dialog } from "@/shared/ui/Dialog";
import { Title } from "@/shared/ui/Title";
import { getFancyTime } from "@/shared/utils/utils";

import { getDetails, getIsLoading } from "../../model/selectors/subscriptionSelectors";
import { unSkipSubscription } from "../../model/services/unSkipSubscription";

import cls from "./PendingSkip.module.scss";

interface PendingSkipProps {
  className?: string;
  onUpdate: () => void;
}

export const PendingSkip = memo(({ className, onUpdate }: PendingSkipProps) => {
  const dispatch = useAppDispatch();
  const user = useUser();

  const isLoading = useSelector(getIsLoading);
  const info = useSelector(getDetails);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(async () => {
    if (isLoading) {
      return;
    }

    const result = await dispatch(unSkipSubscription({ userId: user.id }));

    if (result.meta.requestStatus === "fulfilled") {
      onUpdate();
    }
  }, [onUpdate, dispatch, user, isLoading]);

  return (
    <div className={clsx(cls.PendingSkip, {}, [className])}>
      <div className={cls.Content}>
        <Title>Want more readings?</Title>
        {info && (
          <p>
            You are currently set to skip your subscription on your next billing cycle from{" "}
            {getFancyTime(info.paymentPausedAt)} to {getFancyTime(info.paymentResumesAt)}. You can continue to chat with
            and get readings from your psychics on your existing reading requests until then. To create new reading
            requests with new psychics, please reactivate.
          </p>
        )}
        <Button isLoading={isLoading} onClick={onOpenModal}>
          REACTIVATE
        </Button>
      </div>
      <Dialog
        isOpen={isModalOpen}
        isBusy={isLoading}
        onClose={onCloseModal}
        title="Are you sure you want to reactivate your plan?"
        submit={{
          text: "Yes",
          onClick: async () => {
            await onSubmit();
            onCloseModal();
          },
        }}
        cancel={{ text: "No" }}
      />
    </div>
  );
});
